import { isDefined } from '@morpho-org/morpho-ts'

export const VAULT_V2_INTEREST_PAUSED_LABEL = 'Interest paused'
export const VAULT_V2_INTEREST_PAUSED_TOOLTIP = "The Curator set this Vault's max APY to 0%."

// Shared by the "Capped" tag and the APY breakdown row so their tooltips can't drift.
export const getVaultV2RateCapTooltip = (formattedMaxApy: string) =>
  `This vault's APY is capped at a max rate of ${formattedMaxApy} APY. If this vault earns more than the cap, the surplus is held as a buffer and released over time.`

/**
 * Relative tolerance used when comparing the gross APY to `maxApy`. Both values
 * are computed by the backend from the same on-chain max rate, but float
 * rounding can leave them a hair apart, so we treat `apy` as pinned when it is
 * within this fraction of `maxApy`.
 */
const RATE_CAP_RELATIVE_TOLERANCE = 1e-6

/**
 * Whether a Vault V2's gross APY is pinned to its allocator-set max APY.
 * Underlying markets may earn more; the surplus accrues to the vault as a
 * buffer instead of increasing the displayed APY.
 *
 * @param apy - Gross APY of the vault before fees.
 * @param maxApy - Annualized max APY derived from the allocator cap.
 */
export const isVaultV2RateCapped = (
  apy: number | null | undefined,
  maxApy: number | null | undefined
): boolean => {
  // A non-positive cap can't pin a positive APY in a meaningful way.
  if (!isDefined(apy) || !isDefined(maxApy) || maxApy <= 0) return false

  return apy >= maxApy * (1 - RATE_CAP_RELATIVE_TOLERANCE)
}

/**
 * A Vault V2's gross APY (before fees), recovered from its net APY excluding rewards by inverting
 * Core's `net = expm1(log1p(gross) * (1 - performanceFee) - managementFee)`. Null when the net APY
 * is unknown or the performance fee takes everything, which leaves nothing to invert.
 *
 * @param performanceFee - Fraction of the yield (0.1 is 10%).
 * @param managementFee - Yearly fraction of the assets; null is no fee.
 */
export const toVaultV2GrossApy = (
  netApyExcludingRewards: number | null,
  performanceFee: number,
  managementFee: number | null
): number | null => {
  if (netApyExcludingRewards === null || performanceFee >= 1) return null
  return Math.expm1(
    (Math.log1p(netApyExcludingRewards) + (managementFee ?? 0)) / (1 - performanceFee)
  )
}

export const toVaultV2ApyGross = ({
  apyBreakdown,
  netApyExcludingRewards,
  fees
}: {
  apyBreakdown: { apy: number } | null
  netApyExcludingRewards: number | null
  fees: { performanceFee: number; managementFee: number | null }
}) =>
  apyBreakdown?.apy ??
  toVaultV2GrossApy(netApyExcludingRewards, fees.performanceFee, fees.managementFee)
